import React, { useCallback } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { StyleSheet } from 'react-native';

import { CmsQuestRequirement } from 'components/quests/cms-hooks/types';
import { PressableItemCard } from 'components/quests/components/pressable-item-card';
import { IQuest, IQuestStep } from 'components/quests/loyalty-quest/types';
import { canActivateQuest, isStepCompleted } from 'components/quests/loyalty-quest/utils';
import { ILoyaltyQuestsUiDetailFragment } from 'generated/sanity-graphql';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants';
import { routes } from 'utils/routing';

import { LockedPreview } from './locked-preview';

interface IQuestStepProps {
  step: IQuestStep;
  quest: IQuest;
  // @TODO: Add proper type for cmsStep
  cmsStep: any;
  cmsQuestRequirementById: CmsQuestRequirement;
  cmsQuestDetailUI: ILoyaltyQuestsUiDetailFragment;
  onCloseDrawer: () => void;
  isLocked?: boolean;
}

export const QuestStep = ({
  step,
  quest,
  cmsStep,
  cmsQuestRequirementById,
  cmsQuestDetailUI,
  onCloseDrawer,
  isLocked = false,
}: IQuestStepProps) => {
  const router = useRouter();

  const onPress = useCallback(
    (link?: string, type?: string, id?: string) => () => {
      const path = type && id ? `${routes.menu}/${type}-${id}` : routes.menu;
      onCloseDrawer();
      router.replace(link || path);
    },
    [router, onCloseDrawer]
  );

  const isQuestNotEnrolled = canActivateQuest(quest.status);

  const { cartRequirements, status } = step;
  const isCompletedStep = isStepCompleted(status);
  // Locked steps are non-interactive; reuse `disabled` to block the press
  // and dim the image, then overlay the lock badge via `LockedPreview`.
  const isDisabled = isQuestNotEnrolled || isCompletedStep || isLocked;

  const hasSingleCartRequirement =
    cartRequirements && cartRequirements.length === 1 && cartRequirements[0].length === 1;

  let IconComponent;

  if (!isLocked) {
    if (isCompletedStep) {
      IconComponent = <Icon variant="checkFill" size="7" color={primitive.$crunchyGreen} />;
    } else if (!isQuestNotEnrolled) {
      IconComponent = <Icon variant="forward" size={'4'} />;
    }
  }

  let itemName: any,
    itemImage: any,
    itemLink: string | null | undefined = null;

  if (cmsStep?.title && cmsStep?.image && cmsStep?.link) {
    itemName = cmsStep.title.localeRaw;
    itemImage = cmsStep.image.locale;
    itemLink = cmsStep.link.localeRaw;
  }

  const wrapIfLocked = (card: React.ReactElement) =>
    isLocked ? <LockedPreview>{card}</LockedPreview> : card;

  const lockedName = t('questStepUnlockBonusCrowns', { defaultValue: 'Unlock bonus Crowns' });

  if (hasSingleCartRequirement) {
    const { id: requirementId } = cartRequirements[0][0];
    const cmsRequirement = cmsQuestRequirementById[requirementId];

    return wrapIfLocked(
      <PressableItemCard
        key={step.id}
        style={[styles.pressableItem, isCompletedStep && styles.pressableItemCompleted]}
        onPress={onPress(itemLink || '', cmsRequirement._type, cmsRequirement._id)}
        image={itemImage || cmsRequirement.image}
        altImage={cmsRequirement.imageDescription?.locale ?? ''}
        name={isLocked ? lockedName : (itemName || cmsRequirement?.name?.locale) ?? ''}
        icon={IconComponent}
        disabled={isDisabled}
      />
    );
  }

  const name = isLocked
    ? lockedName
    : isCompletedStep
    ? t('questOrderCompleted')
    : itemName || cmsQuestDetailUI.genericStepText?.locale || t('startOrder');

  return wrapIfLocked(
    <PressableItemCard
      key={step.id}
      style={[styles.pressableItem, isCompletedStep && styles.pressableItemCompleted]}
      onPress={onPress(itemLink || '')}
      image={itemImage || cmsQuestDetailUI.genericStepImage?.locale}
      name={name}
      icon={IconComponent}
      disabled={isDisabled}
    />
  );
};

const styles = StyleSheet.create({
  // Replaces the former `PressableItem` (PressableItemCard.withConfig) from
  // ../styled so styling stays co-located with the component per AGENTS.md.
  // `borderRadius: 8` matches the NativeBase "lg" used previously.
  // The enabled/disabled background is owned by PressableItemCard; the
  // completed override below still wins via the `style` prop.
  pressableItem: {
    borderRadius: 8,
    overflow: 'visible',
    minHeight: 72,
    borderWidth: 1,
    borderColor: primitive.$blackOpacity10,
  },
  pressableItemCompleted: {
    borderWidth: 0,
    backgroundColor: primitive.$blackOpacity4,
  },
});
